import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import type { VisualizationTypesCatalogBody } from '../models/catalog.types'
import type { VisualizationTypeDefinition } from '../models/chart.types'
import { normalizeVisualizationTypes } from '../service/chart/visualizationCatalog'

export type VisualizationTypeCatalogStatus = 'idle' | 'ready' | 'degraded'

/** Versioned backend visualization metadata. Render support is intentionally resolved outside the
 *  store by mapping this open catalog onto module 08's builder registry. */
export const useVisualizationTypeCatalogStore = defineStore('visualizationTypeCatalog', () => {
  const definitions = ref<VisualizationTypeDefinition[]>([])
  const status = ref<VisualizationTypeCatalogStatus>('idle')
  const settled = ref(false)

  const chartDefinitions = computed(() =>
    definitions.value.filter((definition) => definition.kind === 'chart'),
  )
  const tableDefinitions = computed(() =>
    definitions.value.filter((definition) => definition.kind === 'table'),
  )

  function setCatalog(body: VisualizationTypesCatalogBody): void {
    definitions.value = normalizeVisualizationTypes(body)
    status.value = 'ready'
    settled.value = true
  }

  function markDegraded(): void {
    if (status.value !== 'ready') status.value = 'degraded'
    settled.value = true
  }

  /** Warm cache may be displayed only after this boot's manifest decision completes. */
  function beginPrime(): void {
    settled.value = false
  }

  function finishPrime(): void {
    settled.value = true
  }

  function resetState(): void {
    definitions.value = []
    status.value = 'idle'
    settled.value = false
  }

  return {
    definitions,
    chartDefinitions,
    tableDefinitions,
    status,
    settled,
    setCatalog,
    markDegraded,
    beginPrime,
    finishPrime,
    resetState,
  }
})

export type VisualizationTypeCatalogStore = ReturnType<typeof useVisualizationTypeCatalogStore>
